/* Co-Art Studio header: scroll hide/reveal + mobile menu. */

class CoartHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.coart-header');
    this.toggle = this.querySelector('.coart-header__toggle');
    this.drawer = this.querySelector('.coart-header__drawer');

    this.hideAfter = 140;
    this.revealZone = 14;
    this.lastY = window.scrollY;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    document.addEventListener('keydown', this.onKeyDown);

    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.setMenu(!this.menuOpen));
    }

    // A drawer left open across a resize into desktop would keep the scroll
    // lock on a page whose overlay is now display:none.
    this.mq = window.matchMedia('(min-width: 1001px)');
    this.mq.addEventListener('change', (e) => {
      if (e.matches && this.menuOpen) this.setMenu(false);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.menuOpen) this.setMenu(false);
  }

  get menuOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  setMenu(open) {
    if (!this.toggle || !this.drawer) return;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute('aria-label', open ? this.toggle.dataset.labelClose : this.toggle.dataset.labelOpen);
    this.drawer.classList.toggle('is-open', open);
    this.drawer.toggleAttribute('hidden', !open);
    document.body.classList.toggle('coart-menu-open', open);

    // The bar must stay put while the overlay it anchors is on screen.
    if (open) this.show();

    this.setBackgroundInert(open);

    // Skip focus moves when the element is being torn down (section re-render
    // in the theme editor), where the target is already detached.
    if (!this.isConnected) return;

    // The close button sits in the bar, outside the overlay, so the trap has
    // to span the whole custom element or Tab could never reach it.
    if (open) {
      const first = this.drawer.querySelector('.coart-header__drawer-link') || this.toggle;
      if (typeof trapFocus === 'function') {
        trapFocus(this, first);
      } else {
        first.focus();
      }
    } else if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.toggle);
    } else {
      this.toggle.focus();
    }
  }

  /* Tab is trapped, but assistive tech can still reach the page behind the
     overlay by other means. inert takes the rest of the document out. */
  setBackgroundInert(on) {
    if (!('inert' in HTMLElement.prototype)) return;

    if (on) {
      const own = this.closest('.shopify-section') || this;
      this.inerted = Array.from(document.body.children).filter(
        (el) => el !== own && !el.contains(own) && !el.hasAttribute('inert')
      );
      this.inerted.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inerted || []).forEach((el) => el.removeAttribute('inert'));
      this.inerted = [];
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.menuOpen) this.setMenu(false);
  }

  onPointerMove(event) {
    if (event.clientY <= this.revealZone) this.show();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    const y = Math.max(window.scrollY, 0);
    if (this.menuOpen) {
      this.lastY = y;
      return;
    }

    if (y > this.lastY && y > this.hideAfter) {
      this.hide();
    } else if (y < this.lastY) {
      this.show();
    }
    this.lastY = y;
  }

  hide() {
    this.bar?.classList.add('is-hidden');
  }

  show() {
    this.bar?.classList.remove('is-hidden');
  }
}

if (!customElements.get('coart-header')) {
  customElements.define('coart-header', CoartHeader);
}
